import { useState, useEffect, useCallback, useRef } from 'react';
import { EuqTimeRangeEnum } from '../type';

// Storage key for EUQ timeRange
const TIME_RANGE_STORAGE_KEY = 'euq_timerange';

// Helper function to check if a value is a valid enum value
const isValidEnumValue = <T extends Record<string, string>>(
  value: any,
  enumObject: T
): value is T[keyof T] => {
  return Object.values(enumObject).includes(value);
};

// Helper functions for localStorage management
const getTimeRangeFromStorage = (): EuqTimeRangeEnum | null => {
  try {
    const stored = localStorage.getItem(TIME_RANGE_STORAGE_KEY);
    return stored ? (stored as EuqTimeRangeEnum) : null;
  } catch (error) {
    console.warn('Failed to get timeRange from storage:', error);
    return null;
  }
};

const saveTimeRangeToStorage = (timeRange: EuqTimeRangeEnum) => {
  try {
    localStorage.setItem(TIME_RANGE_STORAGE_KEY, timeRange);
  } catch (error) {
    console.warn('Failed to save timeRange to storage:', error);
  }
};

export const usePersistedTimeRange = (
  defaultValue: EuqTimeRangeEnum = EuqTimeRangeEnum.Last1w
) => {
  const isInitializedRef = useRef(false);

  // Get timeRange from localStorage or use default
  const getInitialTimeRange = useCallback(() => {
    const stored = getTimeRangeFromStorage();

    if (!stored) {
      return defaultValue;
    }

    // Validate enum value from storage
    const validTimeRange = isValidEnumValue(stored, EuqTimeRangeEnum)
      ? stored
      : defaultValue;

    return validTimeRange;
  }, [defaultValue]);

  const [timeRange, setTimeRangeState] =
    useState<EuqTimeRangeEnum>(getInitialTimeRange);

  // Initialize from localStorage only once on mount
  useEffect(() => {
    if (!isInitializedRef.current) {
      const initialTimeRange = getInitialTimeRange();
      setTimeRangeState(initialTimeRange);
      isInitializedRef.current = true;
    }
  }, [getInitialTimeRange]);

  // Update localStorage when timeRange changes
  const setTimeRange = useCallback((newTimeRange: EuqTimeRangeEnum) => {
    setTimeRangeState(newTimeRange);
    saveTimeRangeToStorage(newTimeRange);
  }, []);

  return {
    timeRange,
    setTimeRange,
  };
};
